/**
 * The three things a time entry is, rendered as three separate badges.
 *
 * They are three badges and not one composite status because they are three
 * independent facts, and the repository's standing rule is that distinct states are
 * never collapsed:
 *
 *   `task_validation_state`  did anyone manage to check this entry against achau
 *   `status`                 where the entry is in the timer / approval workflow
 *   `source`                 did a machine observe this time or did a person type it
 *
 * An entry can be `stopped` + `manual` + `pending_validation` all at once, and each
 * of the three is somebody different's business. One merged label would have to
 * pick a winner.
 *
 * ── `pending_validation` reads as "we could not check", never as a verdict ──
 *
 * Task 31 keeps three facts apart and this file is where that survives onto the
 * glass:
 *
 *   `pending_validation`  we could not ask — a statement about **us**. achau was
 *                         unreachable when the timer started; the timer ran anyway,
 *                         the row is complete, and a job revalidates later. Severity
 *                         **info**, no action offered, and the copy says the time is
 *                         recorded in full. It is not a warning because nothing is
 *                         wrong with the entry or with the person.
 *   `validation_failed`   we asked and the answer was no. Severity medium, the
 *                         reason shown verbatim — two of the three reasons
 *                         (`task_closed`, `task_not_found`) are not about the
 *                         employee at all — and the copy says the record is kept and
 *                         a manager will look. Task 31's acceptance criterion is
 *                         that nothing is ever deleted, proved by a committed grep
 *                         test; this badge must never imply otherwise.
 *   `validated`           the ordinary case, rendered quietly at severity low.
 *
 * `task_validated_at` is `null` in the pending state, and `openapi.yaml` says why in
 * as many words: "a `pending_validation` entry must not carry a timestamp that reads
 * like the moment somebody confirmed it". So the pending badge's tooltip says "chưa
 * kiểm tra" and never formats a blank.
 */

import type { ReactNode } from 'react';

import { useT } from '../i18n';
import type { MessageKey } from '../i18n/messages';
import type { TaskValidationState, TimeEntrySource, TimerState } from '../api/types';
import { Badge } from '../states/Badge';
import type { IconName, Severity } from '../states/icons';

interface BadgeShape {
  readonly severity: Severity;
  readonly icon: IconName;
  readonly labelKey: MessageKey;
  readonly titleKey: MessageKey;
}

const VALIDATION: Readonly<Record<TaskValidationState, BadgeShape>> = {
  validated: {
    severity: 'low',
    icon: 'circle-check',
    labelKey: 'timer.validation.validated',
    titleKey: 'timer.validation.validated.help',
  },
  pending_validation: {
    severity: 'info',
    icon: 'info',
    labelKey: 'timer.validation.pending',
    titleKey: 'timer.validation.pending.help',
  },
  validation_failed: {
    severity: 'medium',
    icon: 'circle-alert',
    labelKey: 'timer.validation.failed',
    titleKey: 'timer.validation.failed.help',
  },
};

/**
 * The three reasons a validation failed, plus the one that accompanies the pending
 * state. Unknown values fall back to the raw string rather than to silence: a reason
 * this build does not know about is still the answer the employee was given.
 */
const REASON_KEY: Readonly<Record<string, MessageKey>> = {
  achau_unreachable: 'timer.reason.achau_unreachable',
  task_closed: 'timer.reason.task_closed',
  task_not_found: 'timer.reason.task_not_found',
  no_permission: 'timer.reason.no_permission',
};

export function validationReasonText(
  reason: string | null | undefined,
  t: (key: MessageKey) => string,
): string | null {
  if (reason === null || reason === undefined || reason === '') return null;
  const key = REASON_KEY[reason];
  return key === undefined ? reason : t(key);
}

export interface ValidationBadgeProps {
  readonly state: TaskValidationState;
  readonly testId?: string;
}

export function ValidationBadge({ state, testId }: ValidationBadgeProps): ReactNode {
  const t = useT();
  const shape = VALIDATION[state];
  return (
    <Badge
      severity={shape.severity}
      icon={shape.icon}
      label={t(shape.labelKey)}
      title={t(shape.titleKey)}
      {...(testId === undefined ? {} : { testId })}
    />
  );
}

const TIMER_STATE: Readonly<
  Record<TimerState, { severity: Severity; icon: IconName; key: MessageKey }>
> = {
  running: { severity: 'info', icon: 'play', key: 'timer.state.running' },
  paused: { severity: 'info', icon: 'pause', key: 'timer.state.paused' },
  stopped: { severity: 'low', icon: 'square', key: 'timer.state.stopped' },
  pending_approval: { severity: 'info', icon: 'hourglass', key: 'timer.state.pendingApproval' },
  approved: { severity: 'low', icon: 'circle-check', key: 'timer.state.approved' },
  rejected: { severity: 'medium', icon: 'circle-alert', key: 'timer.state.rejected' },
};

export function TimerStateBadge({
  status,
  testId,
}: {
  readonly status: TimerState;
  readonly testId?: string;
}): ReactNode {
  const t = useT();
  const shape = TIMER_STATE[status];
  return (
    <Badge
      severity={shape.severity}
      icon={shape.icon}
      label={t(shape.key)}
      {...(testId === undefined ? {} : { testId })}
    />
  );
}

const SOURCE_KEY: Readonly<Record<TimeEntrySource, MessageKey>> = {
  agent: 'timer.source.agent',
  web: 'timer.source.web',
  manual: 'timer.source.manual',
  api: 'timer.source.api',
};

/**
 * Where the number came from.
 *
 * Rendered on every row rather than only on manual ones, because "no badge" would
 * have to mean "observed" and an absent badge is not a statement. Task 32's rule is
 * that hand-entered time is separated from agent-observed time in every report and
 * every score; a list that shows one and stays silent about the other has separated
 * nothing.
 */
export function SourceBadge({
  source,
  testId,
}: {
  readonly source: TimeEntrySource;
  readonly testId?: string;
}): ReactNode {
  const t = useT();
  return (
    <Badge
      severity="info"
      icon={source === 'manual' ? 'circle-alert' : 'circle-check'}
      label={t(SOURCE_KEY[source])}
      title={t(source === 'manual' ? 'timer.source.manual.help' : 'timer.source.observed.help')}
      {...(testId === undefined ? {} : { testId })}
    />
  );
}
